import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { GET_NOTIFICATIONS, UPDATE_NOTIFY_STATUS } from "../Queries/notify"
import { client } from ".."

const initialState = {
    data: {},
    notificationData: [],
    unreadCount: 0,
    totalNotifications: 0,
    notificationPage: 1,
}

const EmailSocket = createSlice({
    name: "EmailSocket",
    initialState,
    reducers: {
        SetNotification(state, action) {
            state.data = action.payload
        },
        addNotification(state, action) {
            // Deduplicate by id if present
            const existing = action.payload.id
                ? state.notificationData.find(n => n.id === action.payload.id)
                : null;
            if (!existing) {
                state.notificationData.unshift({ ...action.payload, isRead: false });
                state.unreadCount++;
                state.totalNotifications++;
            }
        },
        setNotificationData(state, action) {
            state.notificationData = action.payload;
        },
        setNotificationMeta(state, action) {
            if (action.payload.unreadCount !== undefined) state.unreadCount = action.payload.unreadCount;
            if (action.payload.total !== undefined) state.totalNotifications = action.payload.total;
        },
        markReadLocal(state, action) {
            const { notificationId, type } = action.payload;
            if (notificationId) {
                const item = state.notificationData.find(n => n.id === notificationId);
                if (item && !item.isRead) {
                    item.isRead = true;
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                }
            } else if (type) {
                let count = 0;
                state.notificationData.forEach(n => {
                    if (n.type === type && !n.isRead) {
                        n.isRead = true;
                        count++;
                    }
                });
                state.unreadCount = Math.max(0, state.unreadCount - count);
            } else {
                state.notificationData.forEach(n => { n.isRead = true; });
                state.unreadCount = 0;
            }
        },
        markAllReadLocal(state) {
            state.notificationData.forEach(n => { n.isRead = true; });
            state.unreadCount = 0;
        },
        appendNotifications(state, action) {
            // Append older notifications (pagination)
            const existing = new Set(state.notificationData.map(n => n.id));
            const newItems = action.payload.filter(n => !existing.has(n.id));
            state.notificationData.push(...newItems);
        },
        setNotificationPage(state, action) {
            state.notificationPage = action.payload;
        },
        // Keep backward compat — removeByType still works for legacy callers
        removeByType(state, action) {
            state.notificationData.forEach(n => {
                if (n.type === action.payload && !n.isRead) {
                    n.isRead = true;
                }
            });
            state.unreadCount = state.notificationData.filter(n => !n.isRead).length;
        },
    }
})

export const fetchNotifiesThunk = createAsyncThunk(
    "emailSocket/fetchNotifies",
    async ({ userId, page = 1, limit = 50 }, { dispatch }) => {
        const data = await client.query({
            query: GET_NOTIFICATIONS,
            variables: { userId, page, limit },
            fetchPolicy: "no-cache",
        });
        const result = data.data.getNotifies;
        if (page === 1) {
            dispatch(setNotificationData(result.response || []));
        } else {
            dispatch(appendNotifications(result.response || []));
        }
        dispatch(setNotificationMeta({
            unreadCount: result.unreadCount || 0,
            total: result.total || 0,
        }));
        dispatch(setNotificationPage(page));
    }
);

export const markNotificationRead = createAsyncThunk(
    "emailSocket/markNotificationRead",
    async ({ userId, notificationId, type }, { dispatch }) => {
        try {
            // Optimistic update — mark locally first
            dispatch(markReadLocal({ notificationId, type }));

            await client.mutate({
                mutation: UPDATE_NOTIFY_STATUS,
                variables: { userId, type: type || undefined, notificationId: notificationId || undefined }
            });
        } catch (error) {
            console.error("Error marking notification read:", error);
        }
    }
);

// Backward compat: keep old thunk name working
export const updateNotifyStatus = createAsyncThunk(
    "emailSocket/updateNotifyStatus",
    async ({ userId, type }, { dispatch }) => {
        try {
            dispatch(markReadLocal({ type }));
            await client.mutate({
                mutation: UPDATE_NOTIFY_STATUS,
                variables: { userId, type }
            });
        } catch (error) {
            console.error("Error updating notification status:", error);
        }
    }
);

export const {
    SetNotification,
    addNotification,
    setNotificationData,
    setNotificationMeta,
    markReadLocal,
    markAllReadLocal,
    appendNotifications,
    setNotificationPage,
    removeByType,
} = EmailSocket.actions;

export default EmailSocket.reducer
